<!--
  src/components/PlanDayOrder.vue
  Admin daily view for ONE plan (program type) inside the current course.
  Read-only except SortOrder: number input 1..N + up/down arrows, saved as one ordered list.
  Visual language mirrors the student app's daily view (LearningProgramOverview.vue).

  Embedded inside CourseTasks.vue's plan tab (not a standalone route):
    - programTypeId comes in as a prop (the plan tab owns it).
    - initialDate optionally sets the starting day (e.g. a monthly-calendar day click).
    - The list/monthly/day view toggle lives on the parent (CourseTasks), not here.
  Backend: reuses the existing StructuredPlan endpoints —
    load  = TasksService.getCourseTasks (Search, filtered to the selected day)
    save  = TasksService.reorderStructuredPlanTasks (PlanTasks/Reorder)
-->
<template>
  <div class="PlanDayOrder" dir="rtl">
    <!-- day header -->
    <div class="day-header-box">
      <button type="button" class="day-header-arrow" :disabled="!canGoPrevDay" @click="prevDay">&#x2039;</button>
      <div class="day-header-info">
        <div class="day-number-box"><span class="day-number-num">{{ dayNumber }}</span></div>
        <div>
          <div class="day-label">{{ displayDate }}</div>
          <div class="day-name">{{ dayNameHebrew }}</div>
        </div>
      </div>
      <div class="day-header-progress">
        <span class="day-progress-label">משימות ביום</span>
        <span class="day-progress-count">{{ tasks.length }}</span>
      </div>
      <div class="day-header-progress">
        <span class="day-progress-label">סה״כ זמן</span>
        <span class="day-progress-count" style="color:#17437A">{{ toHours(totalMinutes) }}</span>
      </div>
      <button type="button" class="day-header-arrow" @click="nextDay">&#x203A;</button>
    </div>

    <!-- day note — one note per plan day, separate from each task's own comments -->
    <div class="day-note-box">
      <div class="day-note-head">
        <span class="day-note-title">הערת היום</span>
        <div class="day-note-actions">
          <button v-if="!editingNote" type="button" class="day-note-btn" @click="startEditNote">
            {{ dayComments ? 'ערוך' : 'הוסף הערה' }}
          </button>
          <template v-else>
            <button type="button" class="day-note-btn" :disabled="savingNote" @click="cancelEditNote">בטל</button>
            <button type="button" class="day-note-btn is-primary" :disabled="savingNote" @click="saveNote">
              {{ savingNote ? 'שומר...' : 'שמור' }}
            </button>
          </template>
        </div>
      </div>

      <textarea v-if="editingNote" v-model="noteDraft" class="day-note-input" rows="3"
        placeholder="הערה לאדמין עבור יום זה"></textarea>
      <p v-else class="day-note-text" :class="{ 'is-empty': !dayComments }">
        {{ dayComments || 'אין הערה ליום זה.' }}
      </p>
    </div>

    <!-- task list -->
    <div class="tasks-list">
      <div class="tasks-list-head">
        <span class="tasks-list-title">סדר המשימות ביום</span>
        <span class="tasks-list-hint">הקלד מספר 1–{{ tasks.length }} או השתמש בחצים</span>
      </div>

      <div v-for="(task, i) in tasks" :key="task.id" class="task-card">
        <div class="task-card-main">
          <!-- main text -->
          <div class="task-body">
            <div class="task-title-row">
              <span class="task-subject-chip" :style="{ background: subjectColor(task) }" v-if="task.subjectName">
                {{ task.subjectName }}
              </span>
              <span class="task-title">{{ task.name }}</span>
            </div>
            <div class="task-meta">
              <span v-if="task.entityName">{{ task.entityName }}</span>
              <span v-if="task.entityName" class="sep">·</span>
              <span>{{ task.typeName }}</span>
              <span class="sep">·</span>
              <span>{{ timeWindowLabel(task.timeWindow) }}</span>
              <span class="sep">·</span>
              <span class="muted">#{{ task.taskId }}</span>
            </div>
          </div>

          <!-- comments toggle -->
          <button type="button" class="task-comments-toggle"
            v-if="task.taskComments" @click="toggleTaskComments(task.id)">
            <span class="task-comments-text">הערות</span>
            <span class="task-comments-arrow" :class="{ open: isCommentsOpen(task.id) }">&#9660;</span>
          </button>

          <!-- duration — the RESOLVED value. A pinned duration is marked: without the badge
               an override is invisible state, and a later bank edit that doesn't reach this
               plan reads as a bug. -->
          <b-tooltip :label="durationTooltip(task)" position="is-top" multilined>
            <div class="task-duration" :class="{ 'is-overridden': task.isDurationOverridden }">
              <span class="num">{{ task.durationMinutes || '—' }}</span>
              <span class="unit">דק'</span>
              <span v-if="task.isDurationOverridden" class="ovr">מותאם</span>
            </div>
          </b-tooltip>

          <!-- order control — left edge in RTL -->
          <div class="order-control">
            <input type="number" min="1" :max="tasks.length" class="order-input"
              :value="draftFor(task, i)"
              @input="onDraft(task.id, $event.target.value)"
              @blur="commitDraft(task.id)"
              @keyup.enter="$event.target.blur()" />
            <div class="order-arrows">
              <button type="button" :disabled="i === 0" @click="move(task.id, i)">&#9650;</button>
              <button type="button" :disabled="i === tasks.length - 1" @click="move(task.id, i + 2)">&#9660;</button>
            </div>
          </div>

          <!-- remove this placement from the plan. Bulk removal lives in the list view. -->
          <button type="button" class="task-unassign" title="בטל שיוך"
            :disabled="unassigningId === task.id" @click="unassign(task)">
            <b-icon icon="delete" size="is-small" />
          </button>
        </div>

        <div v-if="task.taskComments && isCommentsOpen(task.id)" class="task-comments-expanded">
          {{ task.taskComments }}
        </div>
      </div>

      <div v-if="!tasks.length && !loading" class="empty-state"><p>אין משימות בתאריך זה.</p></div>
    </div>

    <!-- sticky save bar -->
    <div v-if="isDirty" class="pdo-savebar">
      <span>הסדר שונה ולא נשמר</span>
      <div>
        <b-button label="בטל שינויים" @click="reset" />
        <b-button type="is-primary" label="שמור סדר" :loading="saving" @click="save" />
      </div>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from 'buefy';
import TasksService from '@/services/TasksService';
import TaskDuration from '@/services/TaskDuration';

const WEEK_DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const TIME_WINDOWS = { any: 'כל היום', morning: 'בוקר', evening: 'ערב' };

export default {
  name: 'PlanDayOrder',
  props: {
    // The plan tab owns the program type; passed down so both views stay in sync.
    programTypeId: { type: [Number, String], default: null },
    // Optional starting day (e.g. from a monthly-calendar day click). "YYYY-MM-DD" or Date.
    initialDate: { type: [String, Date], default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      selectedDate: this.initialDate ? new Date(this.initialDate) : new Date(),
      tasks: [],          // working order
      savedIds: [],       // last persisted order
      drafts: {},
      openCommentsId: null,
      // Per-DAY admin note (not a task's own taskComments).
      dayComments: '',
      noteDraft: '',
      editingNote: false,
      savingNote: false,
      // plan-task id currently being removed
      unassigningId: null
    };
  },
  computed: {
    courseId() { return this.$store.state.auth.course.id; },
    dayNumber() { const n = this.selectedDate.getDate(); return n < 10 ? '0' + n : String(n); },
    dayNameHebrew() { return 'יום ' + WEEK_DAY_NAMES[this.selectedDate.getDay()]; },
    displayDate() {
      const d = this.selectedDate;
      return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
    },
    totalMinutes() { return TaskDuration.totalMinutes(this.tasks); },
    isDirty() { return this.tasks.map(t => t.id).join() !== this.savedIds.join(); },
    canGoPrevDay() { return true; }
  },
  created() {
    this.load();
  },
  watch: {
    programTypeId() { this.load(); },
    initialDate(v) { if (v) { this.selectedDate = new Date(v); this.load(); } },
  },
  methods: {
    formatDateStr(d) {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return y + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
    },
    // Map a StructuredPlanTaskModel (from Search) onto the card's field names.
    mapRow(r) {
      return {
        id: r.id,
        taskId: r.tasksBankId != null ? r.tasksBankId : r.taskId,
        name: r.taskName || r.name,
        subjectName: r.subjectName || '',
        subjectColor: r.subjectColor || null,
        entityName: r.entityName || '',
        typeName: r.typeName || r.taskTypeName || '',
        timeWindow: r.timeWindow,
        // Resolved value — what the day total and the pill both use.
        durationMinutes: r.durationMinutes || 0,
        // Override state, kept so the pill can mark a pinned duration and name the bank's value.
        durationMinutesOverride: r.durationMinutesOverride != null ? r.durationMinutesOverride : null,
        isDurationOverridden: !!r.isDurationOverridden,
        bankDurationMinutes: r.bankDurationMinutes != null ? r.bankDurationMinutes : null,
        taskComments: r.taskComments || '',
        sortOrder: r.sortOrder,
      };
    },
    // The per-day note, refetched whenever the selected day or plan changes.
    loadDayComments() {
      if (this.programTypeId == null) return;
      const dateStr = this.formatDateStr(this.selectedDate);
      this.editingNote = false;
      TasksService.getPlanDayComments(this.courseId, this.programTypeId, dateStr)
        .then((r) => {
          // Ignore a response that arrived after the user moved to another day.
          if (this.formatDateStr(this.selectedDate) !== dateStr) return;
          this.dayComments = (r.data && r.data.comments) || '';
        })
        .catch(() => { this.dayComments = ''; });
    },
    startEditNote() {
      this.noteDraft = this.dayComments;
      this.editingNote = true;
    },
    cancelEditNote() {
      this.editingNote = false;
      this.noteDraft = '';
    },
    saveNote() {
      const dateStr = this.formatDateStr(this.selectedDate);
      const text = (this.noteDraft || '').trim();
      this.savingNote = true;
      TasksService.setPlanDayComments({
        CourseId: this.courseId,
        ProgramTypeId: Number(this.programTypeId),
        TaskDate: dateStr,
        Comments: text,
      })
        .then(() => {
          this.dayComments = text;
          this.editingNote = false;
          this.$emit('day-comments-saved', { date: dateStr, comments: text });
          Toast.open({ type: 'is-success', message: text ? 'ההערה נשמרה' : 'ההערה נמחקה', duration: 4000 });
        })
        .catch(() => Toast.open({ type: 'is-danger', message: 'שגיאה בשמירת ההערה', duration: 6000 }))
        .finally(() => { this.savingNote = false; });
    },
    load() {
      if (this.programTypeId == null) return;
      const dateStr = this.formatDateStr(this.selectedDate);
      this.loading = true;
      this.loadDayComments();
      TasksService.getCourseTasks({
        CourseId: this.courseId,
        ProgramTypeId: this.programTypeId,
        FromDate: dateStr,
        ToDate: dateStr,
        PageSize: 9999,
        PageNumber: 1,
        SortedBy: 'sortOrder',
        IsAscending: true,
      })
        .then((r) => {
          const items = (r.data && r.data.items) || [];
          this.tasks = items
            .map(this.mapRow)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          this.savedIds = this.tasks.map(t => t.id);
          this.drafts = {};
          this.openCommentsId = null;
        })
        .catch(() => Toast.open({ type: 'is-danger', message: 'שגיאה בטעינת המשימות', duration: 5000 }))
        .finally(() => { this.loading = false; });
    },
    prevDay() { const d = new Date(this.selectedDate); d.setDate(d.getDate() - 1); this.selectedDate = d; this.load(); },
    nextDay() { const d = new Date(this.selectedDate); d.setDate(d.getDate() + 1); this.selectedDate = d; this.load(); },
    draftFor(task, i) {
      return this.drafts[task.id] !== undefined ? this.drafts[task.id] : String(i + 1);
    },
    onDraft(id, val) { this.$set(this.drafts, id, val); },
    commitDraft(id) {
      const raw = this.drafts[id];
      this.$delete(this.drafts, id);
      const n = parseInt(raw, 10);
      if (!raw || isNaN(n)) return;
      this.move(id, n);
    },
    // toPos is 1-based target position
    move(id, toPos) {
      const list = this.tasks.slice();
      const from = list.findIndex(t => t.id === id);
      if (from < 0) return;
      const to = Math.max(0, Math.min(list.length - 1, toPos - 1));
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      this.tasks = list;
      this.drafts = {};
    },
    reset() { this.load(); },
    save() {
      this.saving = true;
      // Server normalizes to clean 1..N; we send the intended order.
      const rows = this.tasks.map((t, i) => ({ id: t.id, sortOrder: i + 1 }));
      TasksService.reorderStructuredPlanTasks(rows)
        .then(() => {
          this.savedIds = this.tasks.map(t => t.id);
          Toast.open({ type: 'is-success', message: 'הסדר נשמר', duration: 4000 });
        })
        .catch(() => Toast.open({ type: 'is-danger', message: 'שגיאה בשמירת הסדר', duration: 6000 }))
        .finally(() => { this.saving = false; });
    },
    // Remove one placement from the plan. Confirm first — the placement goes, though the
    // task stays linked to the course. Removing several at once is done in the list view.
    unassign(task) {
      this.$buefy.dialog.confirm({
        title: 'ביטול שיוך',
        message: `לבטל את שיוך המשימה "${task.name}" מתאריך זה?`,
        confirmText: 'בטל שיוך',
        cancelText: 'ביטול',
        type: 'is-danger',
        onConfirm: () => {
          this.unassigningId = task.id;
          TasksService.deleteStructuredPlanTask(task.id)
            .then((r) => {
              const d = (r && r.data) || {};
              Toast.open({ type: 'is-success', message: d.message || 'השיוך בוטל', duration: 4000 });
              // Let the parent refresh the calendar/list so all views agree.
              this.$emit('task-unassigned', { date: this.formatDateStr(this.selectedDate), id: task.id });
              this.load();
            })
            .catch(() => Toast.open({ type: 'is-danger', message: 'ביטול השיוך נכשל', duration: 6000 }))
            .finally(() => { this.unassigningId = null; });
        },
      });
    },
    toggleTaskComments(id) { this.openCommentsId = this.openCommentsId === id ? null : id; },
    isCommentsOpen(id) { return this.openCommentsId === id; },
    subjectColor(task) { return task.subjectColor || '#17437A'; },
    timeWindowLabel(w) { return TIME_WINDOWS[w] || ''; },
    durationTooltip(task) {
      if (!task.isDurationOverridden) {
        return 'משך מבנק המשימות — עדכון בבנק יעדכן גם כאן.';
      }
      const bank = task.bankDurationMinutes == null ? '—' : task.bankDurationMinutes;
      return `זמן מותאם לתוכנית זו (בבנק: ${bank} דק'). שינוי המשך בבנק לא ישפיע על משימה זו. ניתן לערוך את המשך בתצוגת הרשימה.`;
    },
    toHours: TaskDuration.toHours
  }
};
</script>

<style scoped>
/* Tokens lifted from the student app's LearningProgramOverview.vue */
.PlanDayOrder {
  --lp-primary: #17437A;
  --lp-radius: 5px;
  --lp-radius-lg: 12px;
  --lp-space-1: 8px;
  --lp-space-2: 16px;
  background: #F0F2F5;
  padding: var(--lp-space-1) 32px 80px;
  font-family: Arimo, Arial, sans-serif;
  direction: rtl;
  text-align: right;
  /* Fill the viewport below the chrome (top bar + tab strip + day bar) so the gray reaches the bottom. */
  min-height: calc(100vh - 186px);
  box-sizing: border-box;
}

/* day header */
.day-header-box {
  position: relative; margin-bottom: var(--lp-space-1); display: flex; flex-wrap: wrap;
  align-items: center; justify-content: space-between; padding: 0.6rem 1rem;
  background: #fff; border-radius: 12px; gap: var(--lp-space-2);
}
.day-header-arrow {
  height: 26px; width: 26px; background: #287DE6; color: #fff; border: none; border-radius: 50%;
  font-size: 18px; cursor: pointer; display: flex; justify-content: center; align-items: center; line-height: 1;
}
.day-header-arrow:disabled { opacity: 0.3; cursor: not-allowed; background: #9e9e9e; }
.day-header-info { display: flex; align-items: center; gap: 0.8rem; flex-grow: 1; }
.day-number-box { border-radius: var(--lp-radius); background: var(--lp-primary); padding: 0.5rem 0.7rem; color: #fff; display: flex; align-items: center; justify-content: center; }
.day-number-num { font-size: 32px; font-weight: 400; line-height: 1; }
.day-label { font-size: 12px; color: #287DE6; }
.day-name { font-size: 20px; color: #000; font-weight: 700; }
.day-header-progress { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; }
.day-progress-label { font-size: 10px; color: var(--lp-primary); }
.day-progress-count { font-size: 24px; font-weight: 700; color: #287DE6; line-height: 1.2; }

/* per-day admin note — sits between the day header and the task list */
.day-note-box {
  background: #fff; border-radius: 12px; padding: 0.7rem 1rem;
  margin-bottom: var(--lp-space-1);
}
.day-note-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.day-note-title { font-size: 14px; font-weight: 700; color: var(--lp-primary); }
.day-note-actions { display: flex; gap: 8px; }
.day-note-btn {
  border: 1px solid #cbd6e2; background: #fff; border-radius: var(--lp-radius);
  color: var(--lp-primary); font-size: 12.5px; font-weight: 600;
  padding: 4px 12px; cursor: pointer;
}
.day-note-btn:hover:not(:disabled) { background: #f0f4f8; }
.day-note-btn:disabled { opacity: 0.5; cursor: default; }
.day-note-btn.is-primary { background: var(--lp-primary); border-color: var(--lp-primary); color: #fff; }
.day-note-btn.is-primary:hover:not(:disabled) { background: #0f3159; }
.day-note-text {
  margin-top: 6px; font-size: 12.5px; color: #555; line-height: 1.55; white-space: pre-wrap;
}
.day-note-text.is-empty { color: #8a97a5; }
.day-note-input {
  margin-top: 6px; width: 100%; box-sizing: border-box; resize: vertical;
  border: 1.5px solid #cbd6e2; border-radius: var(--lp-radius);
  padding: 8px 10px; font-family: Arimo, Arial, sans-serif; font-size: 12.5px;
  color: #363636; outline: none; direction: rtl; text-align: right;
}
.day-note-input:focus { border-color: #287DE6; }

/* list — dense, clean rows so many tasks fit without scrolling */
.tasks-list {
  display: flex; flex-direction: column; background: #fff; gap: 6px;
  padding: 1rem 1.2rem; border-radius: 14px; box-shadow: 0 0 30px 0 rgba(0,0,0,0.15);
}
.tasks-list-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.tasks-list-title { font-size: 15px; font-weight: 700; color: var(--lp-primary); }
.tasks-list-hint { font-size: 12.5px; color: #8a97a5; }

/* No `overflow: hidden` here: it would clip the duration tooltip, which opens above the
   card's top edge. The expanded-comments panel rounds its own bottom corners instead —
   it's the last child, so that's all the clipping was doing. */
.task-card {
  display: flex; flex-direction: column; background: #fff;
  border: 1.5px solid #cdd8e4; border-radius: var(--lp-radius);
  transition: box-shadow 0.12s, border-color 0.12s;
}
.task-card:hover { border-color: #9fb2c6; box-shadow: 0 2px 8px rgba(12,41,73,0.10); }
/* A tooltip opens on hover and overlaps the card above, which is EARLIER in the DOM and so
   paints on top by default. Lift the hovered card (and the open tooltip's own card) above
   its siblings. Stays under .pdo-savebar (z-index: 30). */
.task-card:hover,
.task-card:focus-within { position: relative; z-index: 5; }

.task-card-main {
  display: flex; flex-direction: row; align-items: center; gap: 12px;
  padding: 8px 14px 8px 14px; min-height: 52px;
}

/* order control — divided off on the left (RTL) */
.order-control {
  display: flex; align-items: center; gap: 5px; flex-shrink: 0;
  padding-inline-start: 12px; border-inline-start: 1px dashed #e3e9ef;
}
.order-input {
  width: 42px; height: 34px; text-align: center; font-family: Arimo; font-size: 16px; font-weight: 700;
  color: var(--lp-primary); border: 1.5px solid #cbd6e2; border-radius: var(--lp-radius);
  outline: none; background: #F8FAFC;
}
.order-input::-webkit-outer-spin-button, .order-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.order-input { -moz-appearance: textfield; }
.order-arrows { display: flex; flex-direction: column; gap: 2px; }
.order-arrows button {
  width: 22px; height: 16px; border: 1px solid #cbd6e2; background: #fff; border-radius: 4px;
  color: var(--lp-primary); font-size: 8px; line-height: 1; cursor: pointer; padding: 0;
  display: flex; align-items: center; justify-content: center;
}
.order-arrows button:disabled { border-color: #eef2f6; color: #d3dbe4; cursor: default; }

/* unassign — sits past the order control on the left (RTL), quiet until hovered */
.task-unassign {
  flex-shrink: 0; width: 28px; height: 28px; padding: 0;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid transparent; border-radius: var(--lp-radius);
  background: transparent; color: #9fb2c6; cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}
.task-unassign:hover:not(:disabled) { color: #E1583D; background: #fdf1ef; border-color: #f3cec6; }
.task-unassign:disabled { opacity: 0.4; cursor: default; }

/* main text */
.task-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.task-title-row { display: flex; align-items: center; gap: 8px; min-width: 0; }
.task-subject-chip {
  flex-shrink: 0; max-width: 130px; color: #fff; font-size: 10.5px; font-weight: 700;
  padding: 2px 8px; border-radius: 20px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.task-title { font-size: 14px; font-weight: 700; color: var(--lp-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.task-meta {
  display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #6b7b8c;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  .sep { color: #cdd8e4; }
  .muted { color: #9aa7b4; }
}

/* comments toggle */
.task-comments-toggle {
  display: flex; align-items: center; gap: 5px; background: none; border: none; padding: 0;
  font-size: 12px; font-weight: 500; color: var(--lp-primary); cursor: pointer;
  white-space: nowrap; flex-shrink: 0;
}
.task-comments-text { text-decoration: underline; }
.task-comments-arrow { font-size: 9px; transition: transform 0.2s; display: inline-block; }
.task-comments-arrow.open { transform: rotate(180deg); }

/* duration */
.task-duration {
  flex-shrink: 0; display: flex; align-items: baseline; gap: 3px; justify-content: center;
  padding: 5px 12px; border-radius: 20px; background: #eef3f8; border: 1px solid #dbe4ee;
  .num { font-size: 16px; font-weight: 800; color: var(--lp-primary); }
  .unit { font-size: 11px; font-weight: 600; color: #6b7b8c; }
}

.task-duration.is-overridden {
  background: #eef6f5; border-color: #b9e3de;
  .num { color: #0a7d74; }
}
.task-duration .ovr {
  font-size: 9.5px; font-weight: 700; color: #0a7d74;
  border-inline-start: 1px solid #b9e3de; padding-inline-start: 6px; margin-inline-start: 2px;
}

.task-comments-expanded {
  padding: 10px 16px; font-size: 12.5px; color: #555; line-height: 1.55;
  background: #f7f9fb; border-top: 1px solid #e6ebf1;
  /* Was handled by the card's overflow: hidden; done here so the card can stay unclipped. */
  border-end-start-radius: var(--lp-radius); border-end-end-radius: var(--lp-radius);
}

.empty-state { text-align: center; padding: 24px; color: #8a97a5; }

/* sticky save bar */
.pdo-savebar {
  position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #dbe3ec;
  box-shadow: 0 -4px 20px rgba(12,41,73,0.12); padding: 14px 32px;
  display: flex; align-items: center; gap: 14px; z-index: 30;
  font-size: 13.5px; color: #43535f; font-weight: 600;
}
.pdo-savebar > div { margin-inline-start: auto; display: flex; gap: 10px; }
</style>
